import { z } from "zod";

import {
  CandidateEducationSchema,
  CandidateFinancialSchema,
  CandidateGoalsSchema,
  CandidateHealthSchema,
  CandidateOverseasSchema,
  CandidatePersonalSchema,
  CandidateProfileSchema
} from "../models/generated";

// Re-export enums for consumers
export {
  CandidateEducationLevel,
  CandidateEducationLevelSchema,
  CandidateHealthStatus,
  CandidateHealthStatusSchema,
  CandidateMaritalStatus,
  CandidateMaritalStatusSchema,
  CandidatePassportStatus,
  CandidatePassportStatusSchema
} from "../models/generated";

// Full candidate profile response (GET /profile/me/candidate)
export const CandidateProfileFullSchema = CandidateProfileSchema.extend({
  personal: CandidatePersonalSchema.nullable(),
  education: CandidateEducationSchema.nullable(),
  health: CandidateHealthSchema.nullable(),
  goals: CandidateGoalsSchema.nullable(),
  financial: CandidateFinancialSchema.nullable(),
  overseas: CandidateOverseasSchema.nullable(),
  isComplete: z.boolean()
});
export type CandidateProfileFull = z.infer<typeof CandidateProfileFullSchema>;

// Section upsert schemas (PUT /profile/me/candidate/<section>)
export const UpsertCandidatePersonalSchema = CandidatePersonalSchema.omit({
  userId: true,
  updatedAt: true
}).partial();
export type UpsertCandidatePersonal = z.infer<typeof UpsertCandidatePersonalSchema>;

export const UpsertCandidateEducationSchema = CandidateEducationSchema.omit({
  userId: true,
  updatedAt: true
}).partial();
export type UpsertCandidateEducation = z.infer<typeof UpsertCandidateEducationSchema>;

export const UpsertCandidateHealthSchema = CandidateHealthSchema.omit({
  userId: true,
  updatedAt: true
}).partial();
export type UpsertCandidateHealth = z.infer<typeof UpsertCandidateHealthSchema>;

export const UpsertCandidateGoalsSchema = CandidateGoalsSchema.omit({
  userId: true,
  updatedAt: true
}).partial();
export type UpsertCandidateGoals = z.infer<typeof UpsertCandidateGoalsSchema>;

export const UpsertCandidateFinancialSchema = CandidateFinancialSchema.omit({
  userId: true,
  updatedAt: true
}).partial();
export type UpsertCandidateFinancial = z.infer<typeof UpsertCandidateFinancialSchema>;

export const UpsertCandidateOverseasSchema = CandidateOverseasSchema.omit({
  userId: true,
  updatedAt: true
}).partial();
export type UpsertCandidateOverseas = z.infer<typeof UpsertCandidateOverseasSchema>;

// isProfileComplete helper
export function isProfileComplete(profile: CandidateProfileFull): boolean {
  return (
    profile.education?.educationLevel != null &&
    (profile.goals?.primaryGoals?.length ?? 0) > 0 &&
    profile.personal?.permanentAddress != null &&
    profile.health?.healthStatus != null &&
    profile.health?.maritalStatus != null
  );
}

// Section key union type
export type CandidateSection = "personal" | "education" | "health" | "goals" | "financial" | "overseas";

// Map section -> upsert schema
export const SECTION_SCHEMA_MAP = {
  personal: UpsertCandidatePersonalSchema,
  education: UpsertCandidateEducationSchema,
  health: UpsertCandidateHealthSchema,
  goals: UpsertCandidateGoalsSchema,
  financial: UpsertCandidateFinancialSchema,
  overseas: UpsertCandidateOverseasSchema
} as const;
